import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { Property, UpdatePropertyRequest } from '../types/api';
import { api } from '../services/api';
import { Spinner } from './index';
import { Star, X, Plus, Trash2 } from 'lucide-react';
import { formatFurnished } from '../utils/format';

interface EditableCellProps {
  value: number | undefined;
  label: string;
  onSave: (value: number | undefined) => Promise<void>;
  formatDisplay?: (v: number) => string;
}

function EditableCell({ value, label, onSave, formatDisplay }: EditableCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    setDraft(value != null ? String(value) : '');
    setEditing(true);
  };

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const handleSave = async () => {
    setEditing(false);
    const parsed = draft.trim() === '' ? undefined : parseFloat(draft);
    if (parsed === value) return;
    if (parsed !== undefined && isNaN(parsed)) return;
    await onSave(parsed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') setEditing(false);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        className="compare-editable-input"
        type="number"
        step="any"
        min="0"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        aria-label={label}
      />
    );
  }

  return (
    <span className="compare-editable-cell" onClick={handleClick} role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter') handleClick(); }}>
      {value != null ? (formatDisplay ? formatDisplay(value) : `£${value.toLocaleString()}`) : '—'}
    </span>
  );
}

interface CompareViewProps {
  propertyIds: string[];
  searchId: string;
  onClose: () => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, data: UpdatePropertyRequest) => Promise<void>;
}

export function CompareView({ propertyIds, searchId, onClose, onRemove, onUpdate }: CompareViewProps) {
  const { t } = useTranslation();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchProperties = () => {
    let cancelled = false;
    setLoading(true);
    Promise.all(propertyIds.map(id => api.getProperty(searchId, id)))
      .then(results => {
        if (!cancelled) {
          setProperties(results);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fetchProperties, [propertyIds, searchId]);

  const refreshProperty = async (id: string) => {
    const updated = await api.getProperty(searchId, id);
    setProperties(prev => prev.map(p => p.id === id ? updated : p));
  };

  const handleUpdate = async (id: string, data: UpdatePropertyRequest) => {
    await onUpdate(id, data);
    await refreshProperty(id);
  };

  const handleAddCost = async (prop: Property) => {
    const name = prompt(t('app.compareView.costNamePrompt'));
    if (!name?.trim()) return;
    const amountStr = prompt(t('app.compareView.costAmountPrompt'));
    if (amountStr == null) return;
    const amount = parseFloat(amountStr);
    if (isNaN(amount)) return;
    const newCosts = [...prop.additionalCosts, { name: name.trim(), amount }];
    await handleUpdate(prop.id, { additionalCosts: newCosts });
  };

  const handleRemoveCost = async (prop: Property, index: number) => {
    const newCosts = prop.additionalCosts.filter((_, i) => i !== index);
    await handleUpdate(prop.id, { additionalCosts: newCosts });
  };

  const handleEditCost = async (prop: Property, index: number, field: 'name' | 'amount') => {
    const cost = prop.additionalCosts[index];
    const current = field === 'name' ? cost.name : String(cost.amount);
    const newVal = prompt(field === 'name' ? t('app.compareView.editNamePrompt') : t('app.compareView.editAmountPrompt'), current);
    if (newVal == null || newVal === current) return;
    const newCosts = prop.additionalCosts.map((c, i) => {
      if (i !== index) return c;
      if (field === 'name') return { ...c, name: newVal };
      const parsed = parseFloat(newVal);
      if (isNaN(parsed)) return c;
      return { ...c, amount: parsed };
    });
    await handleUpdate(prop.id, { additionalCosts: newCosts });
  };

  if (loading) {
    return (
      <div className="compare-view">
        <div className="compare-header">
          <h2>{t('app.compareView.header')}</h2>
          <button className="compare-close-btn" onClick={onClose} aria-label={t('app.compareView.closeLabel')}>
            <X size={20} />
          </button>
        </div>
        <div className="loading-state" style={{ padding: '48px 0' }}>
          <Spinner />
          <p>{t('app.compareView.loading')}</p>
        </div>
      </div>
    );
  }

  const computeTotal = (p: Property): number => {
    let total = p.rent;
    if (p.councilTaxYearly != null) total += p.councilTaxYearly / 12;
    if (p.utilitiesMonthly != null) total += p.utilitiesMonthly;
    for (const c of p.additionalCosts) {
      total += c.amount;
    }
    return total;
  };

  const rows: { label: string; render: (p: Property) => React.ReactNode }[] = [
    { label: t('app.compareView.address'), render: p => p.address },
    { label: t('app.compareView.beds'), render: p => p.bedrooms },
    { label: t('app.compareView.baths'), render: p => p.bathrooms },
    { label: t('app.compareView.furnished'), render: p => formatFurnished(p.furnished) },
    { label: t('app.compareView.statusLabel'), render: p => <span className="compare-status">{t(`status.${p.status}`)}</span> },
    {
      label: t('app.compareView.rating'),
      render: p => p.userRating && p.userRating > 0 ? (
        <span className="property-rating">
          {Array.from({ length: p.userRating }, (_, i) => (
            <Star key={i} size={14} fill="currentColor" aria-hidden="true" />
          ))}
        </span>
      ) : '—',
    },
    {
      label: t('app.compareView.tags'),
      render: p => {
        const tags: string[] = [];
        if (p.hasParking) tags.push(t('app.propertyDetail.features.parking'));
        if (p.hasGarden) tags.push(t('app.propertyDetail.features.garden'));
        if (p.petFriendly) tags.push(t('app.propertyDetail.features.petFriendly'));
        if (p.billsIncluded) tags.push(t('app.propertyDetail.features.billsIncluded'));
        if (p.features?.length) tags.push(...p.features);
        return tags.length > 0 ? (
          <div className="compare-tags">
            {tags.map((tag, i) => (
              <span key={i} className="compare-tag">{tag}</span>
            ))}
          </div>
        ) : '—';
      },
    },
    {
      label: t('app.compareView.travel'),
      render: p => p.travelTimes.length > 0 ? (
        <div className="compare-travel">
          {p.travelTimes.map((tt, i) => (
            <div key={i} className="compare-travel-row">
              <span className="compare-travel-name">{tt.locationName}</span>
              {(tt.walkingMinutes > 0 || tt.transitMinutes > 0 || tt.distanceKm > 0) ? (
                <div className="compare-travel-chips">
                  {tt.walkingMinutes > 0 && <span className="travel-chip">{'\u{1F6B6}'} {tt.walkingMinutes}m</span>}
                  {tt.transitMinutes > 0 && <span className="travel-chip">{'\u{1F687}'} {tt.transitMinutes}m</span>}
                  {tt.distanceKm > 0 && <span className="travel-chip">{tt.distanceKm} km</span>}
                </div>
              ) : (
                <span className="compare-travel-no-data">{t('app.compareView.noData')}</span>
              )}
            </div>
          ))}
        </div>
      ) : '—',
    },
  ];

  const maxAdditionalCosts = Math.max(0, ...properties.map(p => p.additionalCosts.length));

  // renderCostSection removed — costs are now rendered as flat grid rows for alignment

  // For mobile cards cost section
  const renderCostSectionMobile = (prop: Property) => (
    <>
      <div className="compare-card-row compare-cost-divider">
        <span className="compare-card-label">{t('app.compareView.rentPerMonth')}</span>
        <span className="compare-card-value">£{prop.rent.toLocaleString()}</span>
      </div>
      <div className="compare-card-row">
        <span className="compare-card-label">{t('app.compareView.councilTax')}</span>
        <span className="compare-card-value">
          <EditableCell
            value={prop.councilTaxYearly}
            label="Council tax yearly"
            onSave={async (v) => handleUpdate(prop.id, { councilTaxYearly: v ?? null })}
            formatDisplay={v => `£${Math.round(v / 12).toLocaleString()}`}
          />
        </span>
      </div>
      <div className="compare-card-row">
        <span className="compare-card-label">{t('app.compareView.utilities')}</span>
        <span className="compare-card-value">
          <EditableCell
            value={prop.utilitiesMonthly}
            label={t('app.compareView.utilities')}
            onSave={async (v) => handleUpdate(prop.id, { utilitiesMonthly: v ?? null })}
          />
        </span>
      </div>
      {prop.additionalCosts.map((cost, i) => (
        <div key={i} className="compare-card-row">
          <span className="compare-card-label compare-cost-name" onClick={() => handleEditCost(prop, i, 'name')} role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter') handleEditCost(prop, i, 'name'); }}>
            {cost.name}
          </span>
          <span className="compare-card-value" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span className="compare-cost-amount" onClick={() => handleEditCost(prop, i, 'amount')} role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter') handleEditCost(prop, i, 'amount'); }}>
              £{cost.amount.toLocaleString()}
            </span>
            <button className="compare-cost-remove" onClick={() => handleRemoveCost(prop, i)} aria-label={`Remove ${cost.name}`}>
              <Trash2 size={12} />
            </button>
          </span>
        </div>
      ))}
      <div className="compare-card-row">
        <span className="compare-card-label" />
        <span className="compare-card-value">
          <button className="compare-add-cost-btn" onClick={() => handleAddCost(prop)}>
            <Plus size={14} /> {t('app.compareView.addCost')}
          </button>
        </span>
      </div>
      <div className="compare-card-row compare-total-row">
        <span className="compare-card-label">{t('app.compareView.totalPerMonth')}</span>
        <span className="compare-card-value">£{Math.round(computeTotal(prop)).toLocaleString()}</span>
      </div>
    </>
  );

  const colCount = properties.length + 1; // label col + property cols

  return (
    <div className="compare-view">
      <div className="compare-header">
        <h2>{t('app.compareView.header')} ({properties.length})</h2>
        <button className="compare-close-btn" onClick={onClose} aria-label={t('app.compareView.closeLabel')}>
          <X size={20} />
        </button>
      </div>

      {/* Desktop: CSS Grid layout — flat children for guaranteed row alignment */}
      <div className="compare-table" style={{ gridTemplateColumns: `minmax(100px, auto) repeat(${colCount - 1}, 1fr)` }}>
        {/* Header row */}
        <div className="compare-cell compare-cell-label compare-cell-header" />
        {properties.map(prop => (
          <div key={prop.id} className="compare-cell compare-cell-header">
            <button className="compare-remove-btn" onClick={() => onRemove(prop.id)} aria-label={t('common.remove')}>
              <X size={14} /> {t('common.remove')}
            </button>
          </div>
        ))}

        {/* Main comparison rows */}
        {rows.map(row => (
          <React.Fragment key={row.label}>
            <div className="compare-cell compare-cell-label">{row.label}</div>
            {properties.map(prop => (
              <div key={prop.id} className="compare-cell">{row.render(prop)}</div>
            ))}
          </React.Fragment>
        ))}

        {/* Cost Breakdown divider */}
        <div className="compare-cell compare-cell-label compare-cost-divider">{t('app.compareView.costBreakdown')}</div>
        {properties.map(prop => (
          <div key={prop.id} className="compare-cell compare-cost-divider" />
        ))}

        {/* Rent */}
        <div className="compare-cell compare-cell-label">{t('app.compareView.rentPerMonth')}</div>
        {properties.map(prop => (
          <div key={prop.id} className="compare-cell">{`£${prop.rent.toLocaleString()} ${t('app.compareView.pcm')}`}</div>
        ))}

        {/* Council Tax */}
        <div className="compare-cell compare-cell-label">{t('app.compareView.councilTax')}</div>
        {properties.map(prop => (
          <div key={prop.id} className="compare-cell">
            <EditableCell
              value={prop.councilTaxYearly}
              label={t('app.compareView.councilTax')}
              onSave={async (v) => handleUpdate(prop.id, { councilTaxYearly: v ?? null })}
              formatDisplay={v => `£${Math.round(v / 12).toLocaleString()}/mo (£${v.toLocaleString()}/yr)`}
            />
          </div>
        ))}

        {/* Utilities */}
        <div className="compare-cell compare-cell-label">{t('app.compareView.utilities')}</div>
        {properties.map(prop => (
          <div key={prop.id} className="compare-cell">
            <EditableCell
              value={prop.utilitiesMonthly}
              label={t('app.compareView.utilities')}
              onSave={async (v) => handleUpdate(prop.id, { utilitiesMonthly: v ?? null })}
            />
          </div>
        ))}

        {/* Additional costs (padded to max) */}
        {Array.from({ length: maxAdditionalCosts }, (_, i) => (
          <React.Fragment key={`cost-${i}`}>
            <div className="compare-cell compare-cell-label">{`Cost ${i + 1}`}</div>
            {properties.map(prop => (
              <div key={prop.id} className="compare-cell">
                {prop.additionalCosts[i] ? (
                  <div className="compare-additional-cost-cell">
                    <span className="compare-cost-name" onClick={() => handleEditCost(prop, i, 'name')} role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter') handleEditCost(prop, i, 'name'); }}>
                      {prop.additionalCosts[i].name}
                    </span>
                    <span className="compare-cost-amount" onClick={() => handleEditCost(prop, i, 'amount')} role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter') handleEditCost(prop, i, 'amount'); }}>
                      £{prop.additionalCosts[i].amount.toLocaleString()}
                    </span>
                    <button className="compare-cost-remove" onClick={() => handleRemoveCost(prop, i)} aria-label={`Remove ${prop.additionalCosts[i].name}`}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </React.Fragment>
        ))}

        {/* Add cost button row */}
        <div className="compare-cell compare-cell-label" />
        {properties.map(prop => (
          <div key={prop.id} className="compare-cell">
            <button className="compare-add-cost-btn" onClick={() => handleAddCost(prop)}>
              <Plus size={14} /> {t('app.compareView.addCost')}
            </button>
          </div>
        ))}

        {/* Total row */}
        <div className="compare-cell compare-cell-label compare-total-row">{t('app.compareView.totalPerMonth')}</div>
        {properties.map(prop => (
          <div key={prop.id} className="compare-cell compare-total-row">
            £{Math.round(computeTotal(prop)).toLocaleString()} /mo
          </div>
        ))}
      </div>

      {/* Mobile: swipeable cards */}
      <div className="compare-cards-mobile">
        {properties.map(prop => (
          <div key={prop.id} className="compare-card">
            <div className="compare-card-header">
              <span className="compare-card-address">{prop.address}</span>
              <button className="compare-remove-btn" onClick={() => onRemove(prop.id)} aria-label="Remove">
                <X size={14} />
              </button>
            </div>
            {rows.map(row => (
              <div key={row.label} className="compare-card-row">
                <span className="compare-card-label">{row.label}</span>
                <span className="compare-card-value">{row.render(prop)}</span>
              </div>
            ))}
            {renderCostSectionMobile(prop)}
          </div>
        ))}
      </div>
    </div>
  );
}
